import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useOptionsFormatter } from "@/utils/optionFormatter";
import { SelectOption, ParticipantData, Alert } from "@/interfaces/interfaces";
import axios from "axios";
import { useModalStore } from "./modal";

interface AlertOption {
    key: number | string;
    value: Alert[];
}

interface AlertsData {
    General: SelectOption[],
    Health: SelectOption[],
    Pension: SelectOption[],
    Annuity: SelectOption[],
    Vacation: SelectOption[],
}

export const useParticipantStore = defineStore("participant", () => {
    // State
    const alerts = ref({
        data: [] as AlertOption[],
        fundData: null as AlertsData | null,
        unreadCount: 0,
        unreadAlerts: false,
    });

    const ssn = ref(null);

    const general = ref<ParticipantData | null>(null);
    const currentStep = ref<number | null>(1);

    const userEmail = ref<string|null>(null);
    const userPhone = ref<string|null>(null);
    const userTwoFaMethod = ref<string|null>(null);
    const oldPreferredMethod = ref<string|null>(null);

    // Actions
    const fetchAlerts = async (limit?: number) => {
        try {
            const response = await axios.get(`api/participant/alerts/${limit}`);
            alerts.value.data = useOptionsFormatter(response.data.Alerts);
            alerts.value.fundData = response.data.UnreadAlertsByFund;
            alerts.value.unreadCount = response.data.UnreadCount;
        } catch (error) {
            console.error("There was an error!", error);
        }
    };

    // Actions
    const getSSN = async (token: string | null) => {
        try {
            const response = await axios.post('api/participant/ssn', { token });
            ssn.value = response.data;
        } catch (error) {
            console.error("There was an error!", error);
        }
    };

    // Get User Email
    const getUserEmail = async () => {
        try {
            const response = await axios.get('api/user/settings');
            userEmail.value = response.data.Email;
            userPhone.value = response.data.Phone;
            userTwoFaMethod.value = response.data.PreferredMethod;
            oldPreferredMethod.value = response.data.PreferredMethod;
        } catch (error) {
            console.error("There was an error!", error);
        }
    };

    // Get User Email
    const checkUser = async (email: string) => {
        try {
            const response = await axios.post('api/user/check-user', {
               email
            });

            return response.data?.success;
        } catch (error: any) {
            console.error("There was an error!", error);
            return error?.response?.data?.errors?.email;
        }
    };

    const fetchGeneralData = async () => {
        try {
            const response = await axios.get("api/participant/general-data");
            general.value = response.data;
        } catch (error) {
            console.error("There was an error!", error);
        }
    };

    const getUnread = () => {
        const unread = alerts.value.data?.map(alert => ({
            ...alert,
            value: alert.value.filter((item: Alert) => Number(item.AlertRead) === 0),
        }))
        .filter(alert => alert.value.length > 0);

        return unread;
    }

    const reset = () => {
        alerts.value = {
            data: [],
            fundData: null,
            unreadCount: 0,
            unreadAlerts: false,
        };
        general.value = null;
        userEmail.value = null;
        userTwoFaMethod.value = null;
    };

    const addressErrors = computed(() => {
        let errorString = '';

        if (!general.value?.AddressFormatted) {
            errorString = 'Missing Primary Address';
        }

        if (general.value?.BadAddress || general.value?.IncompleteAddress) {
            "Primary Address Incorrect/Incomplete."
        }

        return errorString || null;
    })

    const getAddressErrors = (type?: string) => {
        let errorString = '';

        if (!general.value?.AddressFormatted) {
            errorString = 'Missing Primary Address';
        }

        if (general.value?.BadAddress || general.value?.IncompleteAddress) {
            errorString = type === 'profile' ? "Primary Address Incorrect/Incomplete." : "The primary address we have on file for you is either incomplete or incorrect. Please update Address using ‘My Profile-Profile Settings'.";
        }

        return errorString || null;
    }

    // Getters
    const dashboardItems = computed(() => {
        if (!general.value) return [];

        return [
            {
                iconClass: "iatse-icon-profile-circle",
                title: "Participant ID",
                detail: general.value?.ParticipantID,
            },
            {
                iconClass: "iatse-icon-personalcard",
                title: "Name",
                detail: general.value?.Name,
            },
            {
                iconClass: "iatse-icon-cake",
                title: "Date of Birth",
                detail: general.value?.DateofBirth,
            },
            {
                iconClass: "iatse-icon-security-user",
                title: "Age",
                detail: `${general.value?.Age} Yrs`,
            },
            {
                iconClass: "iatse-icon-location",
                title: "Address",
                detail:
                    general.value?.AddressFormatted !== ""
                        ? general.value?.AddressFormatted
                        : "No Address",
                error: getAddressErrors()
            },
        ];
    });

    return {
        getSSN,
        ssn,
        currentStep,
        addressErrors,
        alerts,
        general,
        userEmail,
        userPhone,
        dashboardItems,
        userTwoFaMethod,
        oldPreferredMethod,
        getUnread,
        getUserEmail,
        fetchAlerts,
        getAddressErrors,
        fetchGeneralData,
        reset,
        checkUser,
    };
});
